import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaColumns, FaUndoAlt } from "react-icons/fa";
import "./ItemListTable.css";

function getDefaultColumns(columns) {
  return columns.map((column) => column.key);
}

function readSavedColumns(preferenceKey, columns) {
  const fallback = getDefaultColumns(columns);
  if (!preferenceKey || typeof window === "undefined") return fallback;

  try {
    const saved = JSON.parse(window.localStorage.getItem(preferenceKey) || "{}");
    const allowed = new Set(fallback);
    const stored = Array.isArray(saved.visibleColumns)
      ? saved.visibleColumns.filter((key) => allowed.has(key))
      : [];

    return stored.length ? stored : fallback;
  } catch {
    return fallback;
  }
}

function ColumnSelectorModal({
  columns,
  visibleColumns,
  toggleColumn,
  onReset,
  onClose,
}) {
  return (
    <div className="column-modal-backdrop" onClick={onClose}>
      <div
        className="column-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="column-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="column-modal-header">
          <div className="column-modal-heading">
            <span className="column-modal-eyebrow">List view</span>
            <h3 id="column-modal-title">Customize columns</h3>
            <p>Choose what you want to see. Your selections save automatically on this browser.</p>
          </div>

          <button type="button" className="column-reset-btn" onClick={onReset}>
            <FaUndoAlt /> Reset
          </button>
        </div>

        <div className="column-modal-body">
          <div className="column-option-grid">
            {columns.map((col) => {
              const isVisible = visibleColumns.includes(col.key);

              return (
                <button
                  key={col.key}
                  type="button"
                  className={`column-option ${isVisible ? "active" : ""}`}
                  aria-pressed={isVisible}
                  onClick={() => toggleColumn(col.key)}
                >
                  <span className="column-option-check" aria-hidden="true">
                    {isVisible ? <FaCheck /> : null}
                  </span>
                  <span className="column-option-label">{col.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="column-modal-actions">
          <span>{visibleColumns.length} of {columns.length} columns visible</span>
          <button type="button" onClick={onClose} className="column-done-btn">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ItemListTable({
  items = [],
  columns = [],
  selectedItems = [],
  onRowClick = (..._args) => {},
  multiSelectMode = false,
  preferenceKey = "hobbli:item-list-preferences:guest",
}) {
  const [sortConfig, setSortConfig] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() =>
    readSavedColumns(preferenceKey, columns)
  );
  const [showColumnModal, setShowColumnModal] = useState(false);

  useEffect(() => {
    setVisibleColumns(readSavedColumns(preferenceKey, columns));
  }, [preferenceKey, columns]);

  useEffect(() => {
    if (!preferenceKey || typeof window === "undefined") return;

    let existing = {};
    try {
      existing = JSON.parse(window.localStorage.getItem(preferenceKey) || "{}");
    } catch {
      existing = {};
    }

    window.localStorage.setItem(
      preferenceKey,
      JSON.stringify({ ...existing, visibleColumns })
    );
  }, [preferenceKey, visibleColumns]);

  const displayedColumns = useMemo(
    () => columns.filter((column) => visibleColumns.includes(column.key)),
    [columns, visibleColumns]
  );

  const sortedItems = useMemo(() => {
    if (!sortConfig) return items;
    const { key, direction } = sortConfig;

    return [...items].sort((a, b) => {
      const aVal = a?.[key];
      const bVal = b?.[key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);

      if (bothNumeric) {
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return direction === "asc" ? result : -result;
    });
  }, [items, sortConfig]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        return prev.length === 1 ? prev : prev.filter((column) => column !== key);
      }
      return [...prev, key];
    });
  };

  const resetColumns = () => setVisibleColumns(getDefaultColumns(columns));

  if (!items.length) return <p className="item-table-empty">No items found.</p>;

  return (
    <div className="item-table-wrapper">
      <div className="item-table-toolbar">
        <div className="item-table-toolbar-copy">
          <strong>List view</strong>
          <span>{displayedColumns.length} columns shown</span>
        </div>
        <button
          type="button"
          className="columns-button"
          onClick={() => setShowColumnModal(true)}
        >
          <FaColumns /> Columns
        </button>
      </div>

      {showColumnModal && (
        <ColumnSelectorModal
          columns={columns}
          visibleColumns={visibleColumns}
          toggleColumn={toggleColumn}
          onReset={resetColumns}
          onClose={() => setShowColumnModal(false)}
        />
      )}

      <div className="item-table-container desktop-item-table">
        <table className="item-table">
          <thead>
            <tr>
              {displayedColumns.map((col) => (
                <th
                  key={col.key}
                  className="sticky-header"
                  onClick={() =>
                    setSortConfig((prev) => ({
                      key: col.key,
                      direction:
                        prev?.key === col.key && prev.direction === "asc"
                          ? "desc"
                          : "asc",
                    }))
                  }
                >
                  <span className="table-header-content">
                    {col.label}
                    {sortConfig?.key === col.key && (
                      <span className="sort-indicator" aria-hidden="true">
                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedItems.map((item, index) => {
              const isSelected = selectedItems.some(
                (id) => String(id) === String(item.id)
              );

              return (
                <tr
                  key={item.id}
                  className={isSelected ? "selected" : ""}
                  onClick={(e) => onRowClick(index, item, e)}
                  aria-selected={isSelected}
                >
                  {displayedColumns.map((col) => {
                    const rendered = col.render ? col.render(item) : item[col.key] ?? "";
                    return (
                      <td
                        key={col.key}
                        data-label={col.label}
                        title={String(rendered ?? "")}
                      >
                        {rendered}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {multiSelectMode && (
        <div className="table-selection-hint">
          Click rows to add or remove them from your selection.
        </div>
      )}
    </div>
  );
}
